<template>
  <div class="container mt-5">
    <h2>Envie o arquivo CSV para sugestão de NCM & CEST</h2>

    <div
      class="drop-area border border-primary rounded mb-4"
      @click="triggerFileInput"
      @dragover.prevent
      @drop.prevent="handleDrop"
      @dragenter.prevent="handleDragEnter"
      @dragleave.prevent="handleDragLeave"
      :class="{'drag-active': dragActive}">
      <p class="text-center pt-4">Arraste e solte o arquivo CSV aqui ou clique para fazer upload</p>
      <input type="file" class="d-none" ref="fileInput" @change="handleFileUpload" accept=".csv" />
    </div>

    <div v-if="loading" class="loading-spinner text-center">
      <i class="fas fa-spinner fa-spin fa-3x"></i>
      <p>Processando produtos... {{ progress.toFixed(0) }}%</p>
    </div>

    <button v-if="products.length && !loading" @click="downloadCsv" class="btn btn-primary mb-3">Download CSV Atualizado</button>

    <ul v-if="products.length && !loading">
      <li v-for="product in products" :key="product.Código">
        <strong>{{ product.Nome }}:</strong> {{ product.Categoria }} - {{ product.Tipo }}
        <span v-if="product.NCM">
          <br>NCM: {{ product.NCM }} - {{ product.explanation }}
        </span>
        <span v-else class="text-danger">NCM não encontrado</span>
        <span v-if="product.CEST">
          <br>CEST: {{ product.CEST }} - {{ product.explanation_cest }}
        </span>
        <span v-else class="text-danger">CEST não encontrado</span>
        <span v-if="product.URL">
          <br>URL da Imagem: <a :href="product.Imagem" target="_blank">{{ product.Imagem }}</a>
        </span>
        <span v-else class="text-danger">URL da imagem não encontrada</span>
      </li>
    </ul>

    <div class="modal" tabindex="-1" role="dialog" v-if="modalMessage">
      <div class="modal-dialog" role="document">
        <div class="modal-content" :class="modalSuccess ? 'bg-success' : 'bg-danger'">
          <div class="modal-body">
            <p>{{ modalMessage }}</p>
            <button v-if="!modalSuccess" @click="closeModal" class="btn btn-secondary">Ok</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {fetchImageUrl, fetchNcmSuggestion} from "@/services/NcmService.js";
import jschardet from 'jschardet';

export default {
  data() {
    return {
      products: [],
      loading: false,
      modalMessage: "",
      modalSuccess: false,
      dragActive: false,
      progress: 0,
    };
  },
  methods: {

    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      await this.processFile(file);
    },
    async handleDrop(event) {
      const file = event.dataTransfer.files[0];
      if (!file) return;
      await this.processFile(file);
      this.dragActive = false;
    },
    handleDragEnter() {
      this.dragActive = true;
    },
    handleDragLeave() {
      this.dragActive = false;
    },

    async processFile(file) {
      let parsedProducts = [];

      try {
        const csv = await this.detectAndReadCsvFile(file);
        parsedProducts = this.parseCsv(csv);

        this.loading = true;
        this.progress = 0;

        const productsNeedingSuggestion = parsedProducts.filter(product =>
          product.NCM === "00000000" || product.NCM === "0" || !product.NCM
        );

        const totalProducts = productsNeedingSuggestion.length;
        const batchSize = 10;

        const fetchWithRetry = async (product, retries = 3, delay = 2000) => {
          for (let attempt = 0; attempt < retries; attempt++) {
            try {
              const suggestion = await fetchNcmSuggestion({
                name: product.Nome,
                category: product.Categoria,
                type: product.Tipo
              });
              product.NCM = suggestion.suggested_code;
              product.explanation = suggestion.explanation;
              product.CEST = suggestion.suggested_cest;
              product.explanation_cest = suggestion.explanation_cest;

              if (!product.Categoria || !product.Nome) {
                product.Imagem = null;
                product.errorMessage = "Categoria ou Nome não reconhecidos";
                this.progress += 100 / totalProducts;
                return product;
              }

              product.Imagem = await fetchImageUrl(product.Categoria, product.Nome)

              this.progress += 100 / totalProducts;
              return product;
            } catch (error) {
              if (error.message.includes("503") && attempt < retries - 1) {
                await new Promise(res => setTimeout(res, delay));
              } else {
                throw error;
              }
            }
          }
        };

        for (let i = 0; i < productsNeedingSuggestion.length; i += batchSize) {
          const batch = productsNeedingSuggestion.slice(i, i + batchSize);
          const batchResults = await Promise.allSettled(
            batch.map(product => fetchWithRetry(product))
          );

          batchResults.forEach((result, index) => {
            if (result.status === 'rejected') {
              this.showModal(`Erro no produto ${batch[index].Nome}: ${result.reason.message}`, false);
            }
          });
        }

      } catch (error) {
        this.showModal('Erro durante o processamento do arquivo', false);
      } finally {
        this.products = parsedProducts;
        this.loading = false;
        this.progress = 100;
        this.$eventService.track("dashboard-admin-menu-ai-process-file-to-ncm-suggestion");
        this.showModal('Arquivo processado com sucesso', true);
      }
    },

    arrayBufferToString(buffer) {
      return String.fromCharCode.apply(null, new Uint8Array(buffer));
    },

    async detectAndReadCsvFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const arrayBuffer = reader.result;

          const sampleText = this.arrayBufferToString(arrayBuffer.slice(0, 1024));
          const detectedCharset = jschardet.detect(sampleText).encoding || 'UTF-8';

          if (detectedCharset !== 'UTF-8' && detectedCharset !== 'ISO-8859-1') {
            this.showModal(
              `O charset detectado é ${detectedCharset}. O arquivo pode conter erros de acentuação, considere enviar aquivos do tipo UTF-8 ou ISO-8859-1.`,
              false
            );
          }

          const textDecoder = new TextDecoder(detectedCharset);
          const csv = textDecoder.decode(arrayBuffer);
          resolve(csv);
        };
        reader.onerror = () => reject(new Error("Error reading file"));
        reader.readAsArrayBuffer(file);
      });
    },

    parseCsv(csv) {
      const lines = csv.split('\n').filter(line => line.trim() !== '');
      const headers = lines[0].split(';').map(header => header.trim());
      return lines.slice(1).map(line => {
        const data = line.split(';');
        const product = {};
        headers.forEach((header, index) => {
          product[header] = data[index]?.trim();
        });
        return product;
      });
    },

    downloadCsv() {
      const headers = [
        'Id', 'Código', 'Nome', 'Categoria', 'Valor de Venda', 'Tipo', 'NCM',
        'CFOP', 'CST', 'CEST', 'Código da Nota', 'PIS', 'COFINS', 'Valor de Custo',
        'Codigo Franquia', 'Codigo de Barra', 'Imagem'
      ];
      const csvContent = [headers.join(';')].concat(
        this.products.map(product => [
          product['Id'],
          product['Codigo'],
          product['Nome'],
          product['Categoria'],
          product['Valor de Venda'],
          product['Tipo'],
          product['NCM'],
          product['CFOP'],
          product['CST'],
          product['CEST'],
          product['Codigo da Nota'],
          product['PIS'],
          product['COFINS'],
          product['Valor de Custo'],
          product['Codigo Franquia'],
          product['Codigo de Barra'],
          product['Imagem']
        ].join(';'))
      ).join("\n");

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      this.$eventService.track("dashboard-admin-menu-ai-download-file-with-ncm-suggestion");
      link.setAttribute("download", "produtos_atualizados.csv");
      link.click();
    },

    showModal(message, success) {
      this.modalMessage = message;
      this.modalSuccess = success;
      setTimeout(() => {
        if (success) this.modalMessage = "";
      }, success ? 3000 : null);
    },

    closeModal() {
      this.modalMessage = "";
    }
  }
};
</script>

<style>
.mt-5 {
  margin-top: 3rem;
}

.drop-area {
  padding: 20px;
  border-style: dashed;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.drop-area:hover, .drop-area.drag-active {
  background-color: #f1f1f1;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.modal {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-content.bg-success {
  background-color: #28a745;
}

.modal-content.bg-danger {
  background-color: #dc3545;
}
</style>
